/**
 * Agent Config Dialog Component
 *
 * Form for configuring agent settings (temperature, max_tokens, model).
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Slider,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import agentManagementService from '../../services/agentManagementService';

export default function AgentConfigDialog({ open, agent, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [config, setConfig] = useState({
    temperature: 0.3,
    max_tokens: 100000,
    model: ''
  });

  const [defaultConfig, setDefaultConfig] = useState(null);

  useEffect(() => {
    if (open && agent) {
      loadConfig();
    }
  }, [open, agent]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      const configData = await agentManagementService.getAgentConfig(agent.agent_id);

      setDefaultConfig(configData.default_config);
      setConfig({
        temperature: configData.default_config?.temperature || 0.3,
        max_tokens: configData.default_config?.max_tokens || 100000,
        model: configData.default_config?.model || ''
      });
    } catch (err) {
      console.error('Error loading config:', err);
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      // Build update object with only changed fields
      const updates = {};
      if (defaultConfig) {
        if (config.temperature !== defaultConfig.temperature) {
          updates.temperature = config.temperature;
        }
        if (config.max_tokens !== defaultConfig.max_tokens) {
          updates.max_tokens = config.max_tokens;
        }
        if (config.model !== defaultConfig.model) {
          updates.model = config.model;
        }
      } else {
        // If no default config, send all fields
        updates.temperature = config.temperature;
        updates.max_tokens = config.max_tokens;
        if (config.model) updates.model = config.model;
      }

      if (Object.keys(updates).length === 0) {
        setError('No changes to save');
        return;
      }

      await agentManagementService.updateAgentConfig(agent.agent_id, updates);

      setSuccess(true);
      setTimeout(() => {
        onSave();
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error saving config:', err);
      setError(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setConfig({
      temperature: 0.3,
      max_tokens: 100000,
      model: ''
    });
    setDefaultConfig(null);
    setError(null);
    setSuccess(false);
    onClose();
  };

  const handleReset = () => {
    if (defaultConfig) {
      setConfig({
        temperature: defaultConfig.temperature || 0.3,
        max_tokens: defaultConfig.max_tokens || 100000,
        model: defaultConfig.model || ''
      });
    }
  };

  if (!agent) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Configure {agent.name}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Configuration saved successfully!
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {/* Temperature */}
            <Box>
              <Typography gutterBottom>
                Temperature: {config.temperature}
              </Typography>
              <Slider
                value={config.temperature}
                onChange={(e, value) => setConfig({ ...config, temperature: value })}
                min={0}
                max={2}
                step={0.1}
                marks={[
                  { value: 0, label: '0 (Focused)' },
                  { value: 1, label: '1 (Balanced)' },
                  { value: 2, label: '2 (Creative)' }
                ]}
                valueLabelDisplay="auto"
              />
              <Typography variant="caption" color="text.secondary">
                Controls randomness. Lower values make output more focused and deterministic.
              </Typography>
            </Box>

            {/* Max Tokens */}
            <TextField
              label="Max Tokens"
              type="number"
              value={config.max_tokens}
              onChange={(e) => setConfig({ ...config, max_tokens: parseInt(e.target.value) || 0 })}
              fullWidth
              inputProps={{ min: 100, max: 200000, step: 1000 }}
              helperText="Maximum number of tokens in the response (100-200000)"
            />

            {/* Model Selection */}
            <FormControl fullWidth>
              <InputLabel>Model</InputLabel>
              <Select
                value={config.model}
                onChange={(e) => setConfig({ ...config, model: e.target.value })}
                label="Model"
              >
                <MenuItem value="">
                  <em>Use Default</em>
                </MenuItem>
                <MenuItem value="claude-sonnet-4-20250514">Claude Sonnet 4 (Complex Tasks)</MenuItem>
                <MenuItem value="claude-3-5-haiku-20241022">Claude Haiku (Fast & Cost-Effective)</MenuItem>
                <MenuItem value="gpt-4">GPT-4 (OpenAI)</MenuItem>
                <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo (OpenAI)</MenuItem>
              </Select>
            </FormControl>

            {defaultConfig && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Default Settings:</strong> Temperature: {defaultConfig.temperature},
                  Max Tokens: {defaultConfig.max_tokens},
                  Model: {defaultConfig.model || 'N/A'}
                </Typography>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleReset} disabled={loading || saving}>
          Reset to Default
        </Button>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
